import type { User } from "@/app/types/user";
import type { ImpersonationSurface } from "@/app/types/impersonationErrors";
import { readJwtPayloadLoose } from "@/app/lib/auth/read-jwt-payload-client";
import {
  setToken,
  getToken,
  getAuthRememberMe,
  setUser,
  setOriginalAdmin,
  clearOriginalAdmin,
  authorizationHeaders,
} from "./utils";
import { isClientRoleId } from "@/app/lib/auth/client-session-policy";

function rememberMeForToken(token: string): boolean {
  const payload = readJwtPayloadLoose(token);
  if (payload?.rm === true) return true;
  return getAuthRememberMe();
}

const API_URL = "/api/impersonate";

export type ImpersonationTarget = {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  status: string;
  role: {
    id: number;
    name: string | null;
    role_type_id?: number | null;
  };
};

export type StartImpersonationOptions = {
  surface?: ImpersonationSurface;
};

/**
 * Safely serializes an object for logging, handling circular references and non-serializable values
 */
function safeSerializeForLogging(obj: any): any {
  try {
    // Use JSON.stringify with a replacer to handle circular references and non-serializable values
    return JSON.parse(JSON.stringify(obj, (key, value) => {
      // Handle Error objects
      if (value instanceof Error) {
        return {
          name: value.name,
          message: value.message,
          stack: value.stack,
        };
      }
      // Handle functions
      if (typeof value === "function") {
        return `[Function: ${value.name || "anonymous"}]`;
      }
      // Handle undefined
      if (value === undefined) {
        return "[undefined]";
      }
      // Handle other non-serializable values
      if (typeof value === "symbol") {
        return value.toString();
      }
      return value;
    }));
  } catch (e) {
    // Fallback to string representation if JSON.stringify fails
    return {
      _serializationError: "Failed to serialize object",
      _toString: String(obj),
    };
  }
}

export const impersonateService = {
  async startImpersonation(
    userId: number,
    options?: StartImpersonationOptions
  ): Promise<{ user: User; token: string }> {
    const startTime = Date.now();
    const requestId = `impersonate_${userId}_${startTime}`;
    
    try {
      console.log("[impersonateService.startImpersonation] Starting impersonation", {
        requestId,
        userId,
        timestamp: new Date().toISOString(),
      });


      const response = await fetch(`${API_URL}/start`, {
        credentials: "same-origin",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authorizationHeaders(),
        },
        body: JSON.stringify({
          targetUserId: userId,
          ...(options?.surface ? { surface: options.surface } : {}),
        }),
      });

      console.log("[impersonateService.startImpersonation] Response status:", response.status);

      if (!response.ok) {
        let errorBody: any = {};
        let errorText = "";
        let rawBody: string | null = null;
        
        try {
          // Read the response body as text first
          rawBody = await response.text();
          
          const contentType = response.headers.get("content-type");
          const errorResponseDetails = safeSerializeForLogging({
            status: response.status,
            statusText: response.statusText,
            contentType,
            rawBodyLength: rawBody?.length || 0,
            rawBodyPreview: rawBody?.substring(0, 500),
            requestId,
            userId,
          });
          console.error("[impersonateService.startImpersonation] Error response details:", errorResponseDetails);
          
          if (rawBody && rawBody.trim().length > 0) {
            if (contentType && contentType.includes("application/json")) {
              try {
                errorBody = JSON.parse(rawBody);
              } catch (jsonError) {
                console.error("[impersonateService.startImpersonation] Failed to parse error body as JSON:", jsonError, "rawBody:", rawBody);
                errorText = rawBody;
              }
            } else {
              errorText = rawBody;
            }
          } else {
            console.warn("[impersonateService.startImpersonation] Response body is empty");
            errorText = response.statusText || "Empty response body";
          }
        } catch (parseError) {
          console.error("[impersonateService.startImpersonation] Error reading response body:", parseError);
          errorText = response.statusText || "Failed to read response";
        }
        
        // Extract error message and code from multiple possible formats
        const errorMessage = errorBody.error || errorBody.message || errorText || response.statusText || `Impersonation failed: ${response.status}`;
        const errorCode = errorBody.errorCode;
        
        // Create error object with structured data for better parsing
        const errorObj: any = new Error(errorMessage);
        if (errorCode) {
          errorObj.errorCode = errorCode;
        }
        if (errorBody.details) {
          errorObj.details = errorBody.details;
        }
        // Also attach the full error body for debugging
        errorObj.errorBody = errorBody && Object.keys(errorBody).length > 0 ? errorBody : null;
        errorObj.rawBody = rawBody;
        errorObj.status = response.status;
        
        const errorLogData = safeSerializeForLogging({
          requestId,
          userId,
          status: response.status,
          errorMessage,
          errorCode,
          errorBody: errorBody && Object.keys(errorBody).length > 0 ? errorBody : "empty",
          errorText,
          rawBody: rawBody ? rawBody.substring(0, 200) : "null",
        });
        console.error("[impersonateService.startImpersonation] Error:", errorLogData);
        
        throw errorObj;
      }

      const data = await response.json();
      const duration = Date.now() - startTime;
      console.log("[impersonateService.startImpersonation] Impersonation started", {
        requestId,
        userId: data.user?.id,
        email: data.user?.email,
        duration: `${duration}ms`,
        timestamp: new Date().toISOString(),
      });

      // Store new token and user data (match remember-me from JWT rm claim or prior storage)
      const roleId = data.user?.role?.id;
      const rememberMe = data.token ? rememberMeForToken(data.token) : getAuthRememberMe();
      const persistRememberMe = isClientRoleId(roleId) ? false : rememberMe;
      if (data.token) {
        setToken(data.token, persistRememberMe, roleId);
        const savedToken = getToken();
        if (!savedToken || savedToken !== data.token) {
          console.error("[impersonateService] Token save verification failed!", {
            rememberMe: persistRememberMe,
            expected: data.token.substring(0, 20) + "...",
            actual: savedToken ? savedToken.substring(0, 20) + "..." : "null",
          });
          throw new Error("Failed to save authentication token");
        }
      }

      if (data.user) {
        setUser(data.user, persistRememberMe, roleId);
        // Store original admin if provided
        if (data.user.originalAdmin) {
          setOriginalAdmin(data.user.originalAdmin);
        }
      }

      return data;
    } catch (error) {
      const duration = Date.now() - startTime;
      
      // Properly serialize error for logging
      let errorDetails: any = {
        requestId,
        duration: `${duration}ms`,
        timestamp: new Date().toISOString(),
      };
      
      if (error instanceof Error) {
        errorDetails.error = error.message;
        errorDetails.errorName = error.name;
        errorDetails.errorStack = error.stack;
        
        // Include structured error data if available
        if ("errorCode" in error) {
          errorDetails.errorCode = (error as any).errorCode;
        }
        if ("details" in error) {
          errorDetails.errorDetails = (error as any).details;
        }
        if ("status" in error) {
          errorDetails.status = (error as any).status;
        }
      } else if (typeof error === "object" && error !== null) {
        // Try to extract meaningful information from error object
        errorDetails.error = (error as any).message || (error as any).error || String(error);
        if ("errorCode" in error) {
          errorDetails.errorCode = (error as any).errorCode;
        }
        if ("details" in error) {
          errorDetails.errorDetails = (error as any).details;
        }
      } else {
        errorDetails.error = String(error);
      }
      
      // Handle network errors specifically
      if (error instanceof TypeError && error.message === "Failed to fetch") {
        errorDetails.errorType = "NETWORK_ERROR";
        errorDetails.error = "Network error: Unable to connect to server";
      } else if (error instanceof Error && error.name === "AbortError") {
        errorDetails.errorType = "ABORT_ERROR";
        errorDetails.error = "Request was aborted";
      }
      
      const serializedErrorDetails = safeSerializeForLogging(errorDetails);
      console.error("[impersonateService.startImpersonation] Error during impersonation", serializedErrorDetails);
      throw error;
    }
  },

  async stopImpersonation(): Promise<{ user: User; token: string }> {
    try {
      console.log("[impersonateService.stopImpersonation] Stopping impersonation...");

      const response = await fetch(`${API_URL}/stop`, {
        credentials: "same-origin",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authorizationHeaders(),
        },
      });

      console.log("[impersonateService.stopImpersonation] Response status:", response.status);

      if (!response.ok) {
        let errorBody: Record<string, unknown> = {};
        let errorText = "";
        let rawBody: string | null = null;

        try {
          rawBody = await response.text();
          if (rawBody && rawBody.trim().length > 0) {
            const contentType = response.headers.get("content-type");
            if (contentType?.includes("application/json")) {
              try {
                errorBody = JSON.parse(rawBody) as Record<string, unknown>;
              } catch {
                errorText = rawBody;
              }
            } else {
              errorText = rawBody;
            }
          } else {
            errorText = response.statusText || "Empty response body";
          }
        } catch (parseError) {
          console.warn("[impersonateService.stopImpersonation] Error reading response body:", parseError);
          errorText = response.statusText || "Failed to read response";
        }

        const errorMessage =
          (errorBody.error as string) ||
          (errorBody.message as string) ||
          errorText ||
          response.statusText ||
          `Stop impersonation failed: ${response.status}`;

        const err = new Error(errorMessage) as Error & { status?: number; errorBody?: unknown };
        err.status = response.status;
        err.errorBody = Object.keys(errorBody).length > 0 ? errorBody : null;

        const logData = safeSerializeForLogging({
          status: response.status,
          errorMessage,
          errorBody: Object.keys(errorBody).length > 0 ? errorBody : "empty",
          rawBodyPreview: rawBody?.substring(0, 200) ?? "null",
        });
        console.warn("[impersonateService.stopImpersonation] API error:", logData);

        throw err;
      }

      const data = await response.json();
      console.log("[impersonateService.stopImpersonation] Impersonation stopped:", {
        userId: data.user?.id,
        email: data.user?.email,
      });

      const roleId = data.user?.role?.id;
      const rememberMe = data.token ? rememberMeForToken(data.token) : getAuthRememberMe();
      const persistRememberMe = isClientRoleId(roleId) ? false : rememberMe;
      if (data.token) {
        setToken(data.token, persistRememberMe, roleId);
        const savedToken = getToken();
        if (!savedToken || savedToken !== data.token) {
          console.error("[impersonateService.stopImpersonation] Token save verification failed!", {
            rememberMe: persistRememberMe,
            expected: data.token.substring(0, 20) + "...",
            actual: savedToken ? savedToken.substring(0, 20) + "..." : "null",
          });
          throw new Error("Failed to save authentication token");
        }
      }

      if (data.user) {
        setUser(data.user, persistRememberMe, roleId);
      }

      // Clear original admin data
      clearOriginalAdmin();

      return data;
    } catch (error) {
      console.error("[impersonateService.stopImpersonation] Error stopping impersonation:", error);
      throw error;
    }
  },

  async getImpersonationStatus(): Promise<{
    isImpersonating: boolean;
    impersonatedUser?: User;
    originalAdminId?: number;
  }> {
    try {
      // Only make API calls in the browser
      if (typeof window === "undefined") {
        return { isImpersonating: false };
      }

      console.log("[impersonateService.getImpersonationStatus] Checking impersonation status...");

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      try {
        const response = await fetch(`${API_URL}/status`, {
          credentials: "same-origin",
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...authorizationHeaders(),
          },
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          console.log("[impersonateService.getImpersonationStatus] Status check failed with status:", response.status);
          return { isImpersonating: false };
        }

        const data = await response.json();
        console.log("[impersonateService.getImpersonationStatus] Status:", data.isImpersonating);

        return {
          isImpersonating: data.isImpersonating || false,
          impersonatedUser: data.impersonatedUser,
          originalAdminId: data.originalAdminId,
        };
      } finally {
        // Always clear timeout, even if fetch throws
        clearTimeout(timeoutId);
      }
    } catch (error) {
      // Handle network errors gracefully - don't log as errors if it's just a connection issue
      if (error instanceof TypeError && error.message === "Failed to fetch") {
        // Network error - server might not be ready yet, this is expected on first load
        console.log("[impersonateService.getImpersonationStatus] Network error (server may not be ready), defaulting to not impersonating");
      } else if (error instanceof Error && error.name === "AbortError") {
        // Timeout error
        console.log("[impersonateService.getImpersonationStatus] Request timed out, defaulting to not impersonating");
      } else {
        // Other errors - log them
        console.error("[impersonateService.getImpersonationStatus] Error checking status:", error);
      }
      return { isImpersonating: false };
    }
  },

  isImpersonating(): boolean {
    try {
      // This is a quick check - for accurate status, use getImpersonationStatus
      const userStr = typeof window !== "undefined" 
        ? localStorage.getItem("user") || localStorage.getItem("current_user")
        : null;
      
      if (!userStr) {
        return false;
      }

      const userData = JSON.parse(userStr);
      return userData.isImpersonated === true;
    } catch (error) {
      console.error("[impersonateService.isImpersonating] Error:", error);
      return false;
    }
  },

  async searchTargets(query: string): Promise<ImpersonationTarget[]> {
    const params = new URLSearchParams();
    const trimmed = query.trim();
    if (trimmed) params.set("q", trimmed);
    const qs = params.toString();
    const response = await fetch(`${API_URL}/targets${qs ? `?${qs}` : ""}`, {
      credentials: "same-origin",
      method: "GET",
      headers: {
        ...authorizationHeaders(),
      },
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const error = new Error(
        (body as { error?: string }).error || "Failed to search impersonation targets"
      ) as Error & { status?: number; errorCode?: string };
      error.status = response.status;
      if ((body as { errorCode?: string }).errorCode) {
        error.errorCode = (body as { errorCode: string }).errorCode;
      }
      throw error;
    }
    const data = (await response.json()) as {
      success?: boolean;
      targets?: ImpersonationTarget[];
    };
    return Array.isArray(data.targets) ? data.targets : [];
  },
};

